import { TypographyV3, type TypographyV3Props } from '@jouzen/ecom-components';
import { cx } from 'class-variance-authority';
import { type HTMLMotionProps, useInView } from 'framer-motion';
import { useTranslations } from 'next-intl';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import type { MessageKey } from 'types/LocaleMessageKey';

import Motion from '@/app/components/Motion';

export interface SectionHeadingProps {
  // Conditionally trigger intro animations on each line of text
  readonly animated?: boolean;
  readonly className?: string;
  // The MessageKey for the content of the heading.
  // The value for this should use <br> to separate the lines.
  readonly content: MessageKey;
  // Optional animation props for the italicized line of text
  readonly emAnimationProps?: HTMLMotionProps<'em'>;
  // Tailwind class list providing indentation values for second line
  readonly indentation?: string;
  // Conditionally break the regular and italicized text on to separate lines
  readonly multiline?: boolean;
  // Optional props Typography component that wraps the text
  readonly typographyProps?: Omit<TypographyV3Props, 'children'>;
  // Optional pixel width values of vertical spacing between lines at provided breakpoints
  readonly verticalSpacing?: string;
  // Optional animation props for the wrapper el
  readonly wrapperAnimationProps?: HTMLMotionProps<'div'>;
}

interface EmProps {
  readonly animationProps: HTMLMotionProps<'em'>;
  readonly className: string;
  readonly children: ReactNode;
  readonly shouldAnimate: boolean;
}

const DEFAULT_WRAPPER_ANIMATION_PROPS: HTMLMotionProps<'div'> = {
  animate: { opacity: 1, translateY: 0, filter: 'blur(0px)' },
  initial: { opacity: 0, translateY: 25, filter: 'blur(5px)' },
  transition: { delay: 0.25, duration: 1, type: 'tween' },
};
const DEFAULT_EM_ANIMATION_PROPS: HTMLMotionProps<'em'> = {
  animate: { opacity: 1, translateY: 0, filter: 'blur(0px)' },
  initial: { opacity: 0, translateY: 25, filter: 'blur(5px)' },
  transition: { delay: 0.25, duration: 1, type: 'tween' },
};

const Em = ({
  animationProps,
  className,
  children,
  shouldAnimate,
}: EmProps): JSX.Element => {
  return (
    <Motion
      {...animationProps}
      animate={
        shouldAnimate && animationProps.animate ? animationProps.animate : false
      }
      className={cx('font-serif italic', className)}
      el="em"
      data-cy="section_heading_em"
      initial={
        shouldAnimate && animationProps.initial ? animationProps.initial : false
      }
    >
      {children}
    </Motion>
  );
};

export default function SectionHeading({
  animated,
  className,
  content,
  emAnimationProps = {},
  indentation = '',
  multiline = false,
  typographyProps = {
    Element: 'h1',
    variant: 'h1',
  },
  verticalSpacing = '',
  wrapperAnimationProps = {},
}: SectionHeadingProps): JSX.Element {
  const t = useTranslations();
  const ref = useRef(null);
  const isInView = useInView(ref, { amount: 'all' });
  const [isMultiline, setIsMultiline] = useState<boolean>(false);
  const _wrapperAnimationProps = {
    ...DEFAULT_WRAPPER_ANIMATION_PROPS,
    ...wrapperAnimationProps,
  };
  const _emAnimationProps = {
    ...DEFAULT_EM_ANIMATION_PROPS,
    ...emAnimationProps,
  };
  const shouldAnimate = Boolean(animated) && isInView;

  // Use useEffect to prevent mismatch between SSR and CSR
  useEffect(() => {
    setIsMultiline(multiline);
  }, [multiline]);

  return (
    <Motion
      {...wrapperAnimationProps}
      animate={
        shouldAnimate && _wrapperAnimationProps.animate
          ? _wrapperAnimationProps.animate
          : false
      }
      className={className}
      initial={
        animated && _wrapperAnimationProps.initial
          ? _wrapperAnimationProps.initial
          : false
      }
      data-cy="section_heading"
      ref={ref}
    >
      <TypographyV3 {...typographyProps} data-cy="section_heading_text">
        {t.rich(content, {
          em: function em(chunks: ReactNode) {
            return (
              <Em
                animationProps={_emAnimationProps}
                className={cx({
                  block: isMultiline,
                  [indentation]: isMultiline && indentation,
                  [verticalSpacing]: isMultiline && verticalSpacing,
                })}
                shouldAnimate={shouldAnimate}
              >
                {chunks}
              </Em>
            );
          },
          sup: (chunks) => (
            <sup className="relative -top-4 text-base font-bold md:-top-8 lg:-top-10">
              {chunks}
            </sup>
          ),
        })}
      </TypographyV3>
    </Motion>
  );
}
